/**
 * Tutorial Quiz Data by User Type
 * 
 * Contains quiz questions tailored to each user type:
 * - Installer: Practical installation and commissioning focus
 * - Energy Consultant: Technical analysis and optimization focus  
 * - Sales Person: Customer communication and value proposition focus
 * 
 * Quest 1 (System Familiarization) quiz is shared across all types.
 */

/**
 * Quiz question structure:
 * {
 *   id: unique identifier,
 *   question: the question text,
 *   options: array of 4 answer options,
 *   correctIndex: index of correct answer (0-3),
 *   explanation: explanation shown after answering
 * }
 */

// ============================================================================
// SHARED QUIZ: System Familiarization (Quest 1 - Same for all user types)
// ============================================================================

const SYSTEM_FAMILIARIZATION_QUIZ = {
  title: "System Familiarization Quiz",
  passingScore: 60,
  questions: [
    {
      id: "1-1",
      question: "What does COP stand for in heat pump systems?",
      options: [
        "Cost of Performance",
        "Coefficient of Performance",
        "Cycle of Power",
        "Capacity Output Parameter"
      ],
      correctIndex: 1,
      explanation: "COP stands for Coefficient of Performance. It measures how efficiently a heat pump converts electrical energy into heat energy."
    },
    {
      id: "1-2",
      question: "If a heat pump has a COP of 4.0, what does this mean?",
      options: [
        "It uses 4 kW of electricity per hour",
        "It can heat 4 rooms simultaneously",
        "For every 1 kW of electricity, it delivers 4 kW of heat",
        "It operates at 4% efficiency"
      ],
      correctIndex: 2,
      explanation: "A COP of 4.0 means the heat pump delivers 4 kW of heat for every 1 kW of electrical energy consumed - effectively 400% efficient compared to direct electric heating."
    },
    {
      id: "1-3",
      question: "What happens when heat output from the radiator is less than total heat losses?",
      options: [
        "The room temperature rises",
        "The room temperature falls",
        "The COP increases",
        "Nothing changes"
      ],
      correctIndex: 1,
      explanation: "When heat losses exceed heat input, the room loses more energy than it gains, causing the temperature to fall."
    },
    {
      id: "1-4",
      question: "What is the purpose of the preset valve on a radiator?",
      options: [
        "To turn the radiator on and off",
        "To limit maximum flow rate for hydraulic balancing",
        "To measure water temperature",
        "To display heat output"
      ],
      correctIndex: 1,
      explanation: "The preset valve limits the maximum water flow rate through the radiator, which is essential for hydraulic balancing in multi-radiator systems."
    },
    {
      id: "1-5",
      question: "What does the heat flux overlay visualization show?",
      options: [
        "Electrical consumption of the heat pump",
        "Water flow direction in pipes",
        "Heat flow direction and intensity through the building envelope",
        "Temperature of each room"
      ],
      correctIndex: 2,
      explanation: "The heat flux overlay uses colored arrows to show where and how much heat is escaping from the building through walls, windows, and roof."
    },
    {
      id: "1-6",
      question: "Why is it important to observe system behavior before making adjustments?",
      options: [
        "To save electricity",
        "To prevent costly mistakes and ensure safe operation",
        "To calculate room volume",
        "To clean the filters"
      ],
      correctIndex: 1,
      explanation: "Professional HVAC technicians always observe system behavior first to understand the current operating conditions and prevent making inappropriate adjustments."
    }
  ]
};

// ============================================================================
// INSTALLER QUIZ DATA
// ============================================================================

export const INSTALLER_QUIZ_DATA = {
  1: SYSTEM_FAMILIARIZATION_QUIZ,
  
  2: {
    title: "Design Conditions Setup Quiz",
    passingScore: 60,
    questions: [
      {
        id: "2-1",
        question: "What is the 'design outdoor temperature' (Normaußentemperatur)?",
        options: [
          "The average annual temperature",
          "The coldest temperature the heating system must handle",
          "The most comfortable outdoor temperature",
          "The temperature at which the heat pump stops working"
        ],
        correctIndex: 1,
        explanation: "The design outdoor temperature is the coldest expected temperature that the heating system must be capable of handling while maintaining indoor comfort."
      },
      {
        id: "2-2",
        question: "According to DIN EN 12831, what is the standard design temperature for a living room?",
        options: [
          "18°C",
          "20°C",
          "22°C",
          "24°C"
        ],
        correctIndex: 1,
        explanation: "DIN EN 12831 specifies 20°C as the design indoor temperature for living rooms. Bathrooms require 24°C, bedrooms 18°C."
      },
      {
        id: "2-3",
        question: "If outdoor temperature is -10°C and indoor target is 20°C, what is the design ΔT?",
        options: [
          "10K",
          "20K",
          "30K",
          "40K"
        ],
        correctIndex: 2,
        explanation: "ΔT = T_indoor - T_outdoor = 20°C - (-10°C) = 30K. This temperature difference drives all heat loss calculations."
      },
      {
        id: "2-4",
        question: "Why is it important to use the correct regional design outdoor temperature?",
        options: [
          "It affects the color of the heat pump",
          "Using incorrect values leads to undersized or oversized systems",
          "It's only for paperwork",
          "It doesn't really matter"
        ],
        correctIndex: 1,
        explanation: "Using incorrect design temperatures leads to undersized systems (can't maintain comfort) or oversized systems (wasted money and reduced efficiency)."
      },
      {
        id: "2-5",
        question: "What happens to heat losses when the temperature difference (ΔT) increases?",
        options: [
          "Heat losses decrease",
          "Heat losses stay the same",
          "Heat losses increase proportionally",
          "Heat losses become negative"
        ],
        correctIndex: 2,
        explanation: "Heat loss is directly proportional to ΔT. When the temperature difference doubles, heat losses also double (Q = U × A × ΔT)."
      }
    ]
  },
  
  3: {
    title: "Radiator Sizing for Heat Pumps Quiz",
    passingScore: 60,
    questions: [
      {
        id: "3-1",
        question: "Why do heat pumps typically need larger radiators than gas boilers?",
        options: [
          "Heat pumps are less powerful",
          "Heat pumps work best at lower supply temperatures which reduce radiator output",
          "Larger radiators look better",
          "Building codes require it"
        ],
        correctIndex: 1,
        explanation: "Heat pumps are most efficient at 35-45°C supply temperature, much lower than boiler temperatures of 60-80°C. Lower temperatures mean less heat output per radiator, so larger emitters are needed."
      },
      {
        id: "3-2",
        question: "How much larger do radiators typically need to be for a 40°C heat pump system compared to a 75°C boiler system?",
        options: [
          "Same size",
          "1.5× larger",
          "2-3× larger",
          "10× larger"
        ],
        correctIndex: 2,
        explanation: "At 40°C supply vs 75°C supply, radiators typically need to be 2-3× larger to deliver the same heat output. This is a critical consideration for heat pump retrofits."
      },
      {
        id: "3-3",
        question: "How does supply temperature affect COP?",
        options: [
          "Higher supply temperature improves COP",
          "Supply temperature doesn't affect COP",
          "Lower supply temperature improves COP",
          "COP is only affected by outdoor temperature"
        ],
        correctIndex: 2,
        explanation: "Lower supply temperature means the heat pump works with a smaller temperature lift, which improves COP. Every degree lower increases COP by approximately 2-3%."
      },
      {
        id: "3-4",
        question: "What COP improvement might you expect when lowering supply from 55°C to 40°C?",
        options: [
          "No change",
          "10% improvement",
          "30-40% improvement",
          "100% improvement"
        ],
        correctIndex: 2,
        explanation: "A 15K reduction in supply temperature can improve COP by 30-40% (roughly 2-3% per degree). This makes proper radiator sizing very worthwhile."
      },
      {
        id: "3-5",
        question: "What should you include in a heat pump retrofit quote for a building with old, small radiators?",
        options: [
          "Only the heat pump",
          "A heat pump and radiator upgrades",
          "A larger heat pump to compensate",
          "A recommendation to keep the gas boiler"
        ],
        correctIndex: 1,
        explanation: "Including radiator upgrades in the quote ensures the system works efficiently. A heat pump with inadequate emitters leads to customer complaints and poor COP."
      }
    ]
  },
  
  4: {
    title: "Hydraulic Balancing Quiz",
    passingScore: 60,
    questions: [
      {
        id: "4-1",
        question: "What is the purpose of hydraulic balancing?",
        options: [
          "To make the pump louder",
          "To minimize flow while maintaining heat delivery to all radiators",
          "To increase water pressure",
          "To heat water faster"
        ],
        correctIndex: 1,
        explanation: "Hydraulic balancing ensures each radiator receives the correct flow rate for its design heat output, minimizing pump energy and ensuring even heat distribution."
      },
      {
        id: "4-2",
        question: "Using Q = ṁ × c_p × ΔT, if you double the temperature difference (ΔT), what happens to required flow (ṁ)?",
        options: [
          "Flow must double",
          "Flow can be halved for the same heat output",
          "Flow stays the same",
          "Flow becomes zero"
        ],
        correctIndex: 1,
        explanation: "Since Q = ṁ × c_p × ΔT, if ΔT doubles and Q stays constant, ṁ must halve. Larger ΔT allows lower flow for the same heat transfer."
      },
      {
        id: "4-3",
        question: "How much can hydraulic balancing reduce pump energy consumption?",
        options: [
          "5-10%",
          "20-30%",
          "50-80%",
          "It doesn't affect pump energy"
        ],
        correctIndex: 2,
        explanation: "Properly balanced systems can reduce pump energy by 50-80% compared to unbalanced systems running at full flow."
      },
      {
        id: "4-4",
        question: "What happens to return temperature when you reduce flow rate through a radiator?",
        options: [
          "Return temperature increases",
          "Return temperature decreases (more heat extracted)",
          "Return temperature stays the same",
          "Return temperature becomes unpredictable"
        ],
        correctIndex: 1,
        explanation: "Lower flow means water spends more time in the radiator, losing more heat and returning at a lower temperature (larger supply-return ΔT)."
      },
      {
        id: "4-5",
        question: "What does the preset valve position number indicate?",
        options: [
          "Temperature setting",
          "Flow restriction level - lower numbers = more restricted",
          "Room number",
          "Power consumption"
        ],
        correctIndex: 1,
        explanation: "Preset valve positions (typically 1-7) control flow restriction. Lower numbers restrict more flow, higher numbers allow more flow."
      },
      {
        id: "4-6",
        question: "Why should you balance the system during commissioning?",
        options: [
          "It's not necessary",
          "It reduces pump energy, noise, and ensures even heat distribution",
          "It voids the warranty if not done",
          "Only for commercial buildings"
        ],
        correctIndex: 1,
        explanation: "Balancing during commissioning ensures optimal system performance from day one - lower bills, quieter operation, and happy customers."
      }
    ]
  },
  
  5: {
    title: "Ventilation Impact Assessment Quiz",
    passingScore: 60,
    questions: [
      {
        id: "5-1",
        question: "What does ACH stand for?",
        options: [
          "Air Conditioning Hours",
          "Air Changes per Hour",
          "Average Climate Heat",
          "Automatic Control Hub"
        ],
        correctIndex: 1,
        explanation: "ACH (Air Changes per Hour) measures how many times the entire room air volume is replaced with fresh air in one hour."
      },
      {
        id: "5-2",
        question: "In a well-insulated building, what proportion of heat loss might be due to ventilation?",
        options: [
          "Less than 10%",
          "About 25%",
          "40-60% or more",
          "Exactly 5%"
        ],
        correctIndex: 2,
        explanation: "In well-insulated buildings, transmission losses through the envelope are reduced, making ventilation often 40-60% or more of total heat loss."
      },
      {
        id: "5-3",
        question: "If a heat pump isn't keeping up with demand, what should you check first?",
        options: [
          "The refrigerant brand",
          "Obvious air leaks: drafty windows, unsealed penetrations",
          "The nameplate",
          "Paint color"
        ],
        correctIndex: 1,
        explanation: "Many 'heat pump problems' are actually building ventilation/air leakage issues. Check for obvious leaks before blaming the equipment."
      },
      {
        id: "5-4",
        question: "What happens to ventilation heat loss if you reduce ACH from 2.0 to 1.0?",
        options: [
          "It stays the same",
          "It doubles",
          "It halves",
          "It becomes zero"
        ],
        correctIndex: 2,
        explanation: "Ventilation heat loss is directly proportional to ACH. Halving the air change rate halves the ventilation heat loss."
      },
      {
        id: "5-5",
        question: "What ACH value typically indicates a leaky building needing air sealing?",
        options: [
          "0.3 ACH",
          "0.7 ACH",
          "2.0+ ACH",
          "1.0 ACH"
        ],
        correctIndex: 2,
        explanation: "ACH of 2.0 or higher indicates a leaky building. Modern targets are 0.5-1.0 ACH, with passive houses achieving 0.3-0.5 ACH."
      }
    ]
  },
  
  6: {
    title: "Commissioning Verification Quiz",
    passingScore: 70,
    questions: [
      {
        id: "6-1",
        question: "What COP value represents professional-grade heat pump efficiency under design conditions?",
        options: [
          "COP 2.0",
          "COP 3.0",
          "COP 4.5+",
          "COP 10.0"
        ],
        correctIndex: 2,
        explanation: "COP 4.5+ at design conditions (ΔT = 30K) represents excellent optimization. Many installations only achieve 2.5-3.5 due to poor design."
      },
      {
        id: "6-2",
        question: "Why should you document commissioning values?",
        options: [
          "For decoration",
          "For legal protection and customer proof of performance",
          "It's optional",
          "To satisfy curiosity"
        ],
        correctIndex: 1,
        explanation: "Commissioning documentation protects you legally, proves performance to the customer, and provides a baseline for future troubleshooting."
      },
      {
        id: "6-3",
        question: "What does it indicate if supply temperature >45°C is needed to meet heat demand?",
        options: [
          "Excellent system design",
          "Perfect sizing",
          "The emitters may be undersized or building poorly insulated",
          "Maximum efficiency achieved"
        ],
        correctIndex: 2,
        explanation: "Needing high supply temperature suggests undersized emitters or excessive heat losses. Investigate before handover to prevent callbacks."
      },
      {
        id: "6-4",
        question: "During commissioning, room temperature should be within what range of setpoint?",
        options: [
          "±10°C",
          "±5°C",
          "±2°C",
          "Exactly equal"
        ],
        correctIndex: 2,
        explanation: "Room temperature within ±2°C of setpoint indicates stable system operation with adequate capacity and control."
      },
      {
        id: "6-5",
        question: "What separates a professional installation from a basic one?",
        options: [
          "The color of the outdoor unit",
          "Achieving COP 4.5+ through attention to all efficiency factors",
          "Having more remote controls",
          "Using premium refrigerant"
        ],
        correctIndex: 1,
        explanation: "Professional installations achieve 50%+ better efficiency through careful attention to supply temp, emitter sizing, building envelope, and system balancing."
      }
    ]
  }
};

// ============================================================================
// ENERGY CONSULTANT QUIZ DATA
// ============================================================================

export const ENERGY_CONSULTANT_QUIZ_DATA = {
  1: SYSTEM_FAMILIARIZATION_QUIZ,
  
  2: {
    title: "Building Energy Standards Quiz",
    passingScore: 60,
    questions: [
      {
        id: "2-1",
        question: "What standard defines design conditions for heating load calculations?",
        options: [
          "DIN EN 1000",
          "DIN EN 12831",
          "ISO 9001",
          "ASHRAE 90.1"
        ],
        correctIndex: 1,
        explanation: "DIN EN 12831 is the European standard specifying design conditions for heating load calculations, including reference outdoor temperatures and indoor comfort temperatures."
      },
      {
        id: "2-2",
        question: "How does design outdoor temperature vary?",
        options: [
          "It's the same everywhere",
          "It varies by building age",
          "It varies by geographic location/climate zone",
          "It changes daily"
        ],
        correctIndex: 2,
        explanation: "Design outdoor temperature varies by location. Coastal areas might use -5°C, moderate inland -10°C, and mountain regions -15°C or colder."
      },
      {
        id: "2-3",
        question: "Why are different indoor temperatures specified for different room types?",
        options: [
          "For energy savings only",
          "Based on room function and occupant activity levels/clothing",
          "Due to window sizes",
          "Building code bureaucracy"
        ],
        correctIndex: 1,
        explanation: "Room temperatures reflect occupant needs: bathrooms 24°C (undressing), living rooms 20°C (sedentary), bedrooms 18°C (sleeping comfort)."
      },
      {
        id: "2-4",
        question: "What is the relationship between ΔT and heat loss?",
        options: [
          "Inversely proportional",
          "No relationship",
          "Directly proportional - double ΔT means double heat loss",
          "Logarithmic"
        ],
        correctIndex: 2,
        explanation: "Heat loss Q = U × A × ΔT is directly proportional to temperature difference. Doubling ΔT doubles the heat loss."
      },
      {
        id: "2-5",
        question: "For energy assessments, why use standardized conditions?",
        options: [
          "To make calculations easier",
          "For comparability, code compliance, and credibility",
          "Because clients expect it",
          "To increase costs"
        ],
        correctIndex: 1,
        explanation: "Standardized conditions ensure assessments are comparable, comply with regulations, and provide credible recommendations."
      }
    ]
  },
  
  3: {
    title: "Building Envelope Analysis Quiz",
    passingScore: 60,
    questions: [
      {
        id: "3-1",
        question: "What does U-value measure?",
        options: [
          "Ultimate temperature capacity",
          "Thermal transmittance - rate of heat flow through a material",
          "Utility usage",
          "Ultraviolet protection"
        ],
        correctIndex: 1,
        explanation: "U-value (thermal transmittance) measures how easily heat passes through a building element in W/(m²·K). Lower U-values indicate better insulation."
      },
      {
        id: "3-2",
        question: "What is the relationship between U-value and R-value?",
        options: [
          "They are the same thing",
          "R = 1/U - they are inverses",
          "R = U × 2",
          "There is no relationship"
        ],
        correctIndex: 1,
        explanation: "R-value (thermal resistance) is the inverse of U-value: R = 1/U. Higher R means better insulation, while lower U means better insulation."
      },
      {
        id: "3-3",
        question: "What is a typical U-value for a well-insulated modern wall?",
        options: [
          "1.5 W/(m²·K)",
          "0.8 W/(m²·K)",
          "0.2 W/(m²·K)",
          "5.0 W/(m²·K)"
        ],
        correctIndex: 2,
        explanation: "Modern building standards require wall U-values around 0.2 W/(m²·K). Older uninsulated walls might have U-values of 1.0-2.0 W/(m²·K)."
      },
      {
        id: "3-4",
        question: "Calculate heat loss for: U=0.3 W/(m²·K), A=40m², ΔT=25K",
        options: [
          "100 W",
          "200 W",
          "300 W",
          "400 W"
        ],
        correctIndex: 2,
        explanation: "Q = U × A × ΔT = 0.3 × 40 × 25 = 300 W. This is the transmission heat loss through this element at these conditions."
      },
      {
        id: "3-5",
        question: "Why prioritize envelope improvements before sizing a heat pump?",
        options: [
          "It's cheaper to install insulation",
          "Reduced demand enables smaller equipment and lower supply temperatures",
          "Regulations require it",
          "Heat pumps don't work without insulation"
        ],
        correctIndex: 1,
        explanation: "Better insulation reduces heat demand, allowing a smaller, cheaper heat pump operating at lower supply temperature with higher COP. Often the best ROI."
      },
      {
        id: "3-6",
        question: "If wall area doubles while U-value and ΔT stay constant, heat loss:",
        options: [
          "Stays the same",
          "Doubles",
          "Halves",
          "Quadruples"
        ],
        correctIndex: 1,
        explanation: "Heat loss Q = U × A × ΔT is directly proportional to area. Doubling the area doubles the heat loss."
      }
    ]
  },
  
  4: {
    title: "Ventilation Energy Impact Quiz",
    passingScore: 60,
    questions: [
      {
        id: "4-1",
        question: "In a modern, well-insulated building, ventilation often accounts for:",
        options: [
          "Less than 10% of heat loss",
          "About 25% of heat loss",
          "50-60% or more of heat loss",
          "Exactly 75% of heat loss"
        ],
        correctIndex: 2,
        explanation: "In well-insulated buildings with low transmission losses, ventilation often dominates total heat loss at 50-60% or more."
      },
      {
        id: "4-2",
        question: "What efficiency can modern Heat Recovery Ventilation (HRV) systems achieve?",
        options: [
          "10-20%",
          "30-50%",
          "70-90%",
          "100%"
        ],
        correctIndex: 2,
        explanation: "Modern HRV systems can recover 70-90% of the heat from exhaust air, significantly reducing ventilation heat loss while maintaining air quality."
      },
      {
        id: "4-3",
        question: "What ACH range is typical for passive house standard?",
        options: [
          "2.0-3.0 ACH",
          "1.0-1.5 ACH",
          "0.3-0.5 ACH",
          "0.1 ACH"
        ],
        correctIndex: 2,
        explanation: "Passive houses achieve 0.3-0.5 ACH through excellent air sealing, combined with mechanical ventilation for controlled air quality."
      },
      {
        id: "4-4",
        question: "For a client with ACH > 1.5, what is usually the best first investment?",
        options: [
          "A bigger heat pump",
          "Air sealing improvements",
          "More radiators",
          "Smart thermostat"
        ],
        correctIndex: 1,
        explanation: "Air sealing to reduce ACH from >1.5 to ~1.0 can halve ventilation losses, often providing better ROI than upgrading equipment."
      },
      {
        id: "4-5",
        question: "If HRV recovers 80% of ventilation heat, what is the effective ventilation loss?",
        options: [
          "80% of original",
          "50% of original",
          "20% of original",
          "0%"
        ],
        correctIndex: 2,
        explanation: "With 80% heat recovery, only 20% of the ventilation heat is lost. For 1 kW original loss, effective loss is just 200 W."
      }
    ]
  },
  
  5: {
    title: "COP Optimization Analysis Quiz",
    passingScore: 60,
    questions: [
      {
        id: "5-1",
        question: "What is the Carnot COP formula for a heat pump?",
        options: [
          "COP = T_cold / T_hot",
          "COP = T_hot / (T_hot - T_cold)",
          "COP = (T_hot - T_cold) / T_hot",
          "COP = T_hot × T_cold"
        ],
        correctIndex: 1,
        explanation: "Carnot COP = T_hot / (T_hot - T_cold), where temperatures are in Kelvin. Real heat pumps achieve 60-75% of this theoretical maximum."
      },
      {
        id: "5-2",
        question: "By approximately how much does COP improve for each degree the supply temperature is lowered?",
        options: [
          "0.1%",
          "2-3%",
          "10%",
          "25%"
        ],
        correctIndex: 1,
        explanation: "As a rule of thumb, each degree reduction in supply temperature improves COP by approximately 2-3%."
      },
      {
        id: "5-3",
        question: "If COP improves from 3.0 to 4.5, what is the electricity reduction?",
        options: [
          "10%",
          "25%",
          "33%",
          "50%"
        ],
        correctIndex: 2,
        explanation: "Electricity use = Heat / COP. Going from COP 3 to COP 4.5 reduces electricity from 33.3% to 22.2% of heat delivered — a 33% reduction."
      },
      {
        id: "5-4",
        question: "What supply temperature range is optimal for heat pump efficiency?",
        options: [
          "70-80°C",
          "55-65°C",
          "35-45°C",
          "20-25°C"
        ],
        correctIndex: 2,
        explanation: "35-45°C is the optimal range for heat pump efficiency, achieving high COP while still providing adequate heat with properly sized emitters."
      },
      {
        id: "5-5",
        question: "How should you present COP improvements to clients?",
        options: [
          "Only show the COP numbers",
          "Calculate annual cost savings over the system lifetime",
          "Don't mention efficiency",
          "Focus only on equipment cost"
        ],
        correctIndex: 1,
        explanation: "Clients respond best to concrete financial benefits. Show annual €/kWh savings and 20-year total cost impact to demonstrate real value."
      },
      {
        id: "5-6",
        question: "What trade-off must be calculated for lower supply temperatures?",
        options: [
          "Room color changes",
          "Larger emitters needed vs. efficiency gains",
          "Noise levels",
          "Maintenance frequency"
        ],
        correctIndex: 1,
        explanation: "Lower supply temperatures require larger emitters (cost). Calculate if the long-term efficiency gains outweigh the extra capital investment."
      }
    ]
  },
  
  6: {
    title: "Complete System Assessment Quiz",
    passingScore: 70,
    questions: [
      {
        id: "6-1",
        question: "What distinguishes professional energy consulting from equipment sales?",
        options: [
          "Charging higher fees",
          "Comprehensive analysis covering all system factors",
          "Using complicated language",
          "Only recommending premium brands"
        ],
        correctIndex: 1,
        explanation: "Professional consultants analyze envelope, ventilation, emitters, controls, and integration — not just equipment selection."
      },
      {
        id: "6-2",
        question: "Which factor has the GREATEST impact on heat pump system efficiency?",
        options: [
          "Heat pump brand",
          "Paint color",
          "Supply temperature (keeping it low)",
          "Thermostat model"
        ],
        correctIndex: 2,
        explanation: "Supply temperature is the single most important factor affecting COP. Proper system design to enable low supply temperatures maximizes efficiency."
      },
      {
        id: "6-3",
        question: "What COP should a professionally assessed system achieve at design conditions?",
        options: [
          "COP 2.0",
          "COP 3.0",
          "COP 4.5+",
          "COP 10.0"
        ],
        correctIndex: 2,
        explanation: "COP 4.5+ at design conditions (ΔT = 30K) represents professional-level optimization, achievable through holistic system design."
      },
      {
        id: "6-4",
        question: "The complete assessment checklist should include:",
        options: [
          "Just the heat pump specification",
          "Building envelope, ventilation, supply temp, emitters, and controls",
          "Only annual cost estimate",
          "Equipment warranties"
        ],
        correctIndex: 1,
        explanation: "Professional assessment covers all factors affecting system performance: envelope analysis, ventilation evaluation, emitter sizing, control strategy, and integration."
      },
      {
        id: "6-5",
        question: "Why prepare a professional report template?",
        options: [
          "To look more professional",
          "To ensure consistent, comprehensive analysis that justifies fees",
          "Requirements by law",
          "To confuse clients"
        ],
        correctIndex: 1,
        explanation: "A structured report ensures you cover all factors consistently, demonstrates value to clients, and justifies professional consulting fees."
      }
    ]
  }
};

// ============================================================================
// SALES PERSON QUIZ DATA
// ============================================================================

export const SALES_PERSON_QUIZ_DATA = {
  1: SYSTEM_FAMILIARIZATION_QUIZ,
  
  2: {
    title: "Understanding Heat Pump Efficiency Quiz",
    passingScore: 60,
    questions: [
      {
        id: "2-1",
        question: "How should you explain COP to a customer?",
        options: [
          "Use the technical formula",
          "'For every €1 of electricity, you get €3-4 worth of heat'",
          "Don't mention efficiency",
          "Compare to car engine HP"
        ],
        correctIndex: 1,
        explanation: "Converting COP to money terms makes the benefit tangible for customers. They understand cost savings better than technical ratios."
      },
      {
        id: "2-2",
        question: "If a heat pump has COP 4, what is the effective efficiency compared to direct electric heating?",
        options: [
          "4%",
          "40%",
          "100%",
          "400%"
        ],
        correctIndex: 3,
        explanation: "COP 4 means 4 units of heat per unit of electricity — 400% effective efficiency compared to direct electric heating at 100%."
      },
      {
        id: "2-3",
        question: "What happens to COP in mild weather (e.g., outdoor temp 5°C)?",
        options: [
          "COP decreases",
          "COP stays the same",
          "COP increases significantly",
          "COP becomes negative"
        ],
        correctIndex: 2,
        explanation: "In mild weather, the temperature lift is smaller, so COP increases. This means even better efficiency during the majority of the heating season."
      },
      {
        id: "2-4",
        question: "Compared to a gas boiler at €0.10/kWh, what is the heat pump cost at COP 4 with electricity at €0.30/kWh?",
        options: [
          "€0.30/kWh heat",
          "€0.10/kWh heat",
          "€0.075/kWh heat",
          "€0.40/kWh heat"
        ],
        correctIndex: 2,
        explanation: "At COP 4, cost per kWh heat = €0.30 / 4 = €0.075/kWh — cheaper than gas at €0.10! This is your key sales message."
      },
      {
        id: "2-5",
        question: "Why is live demonstration better than brochures?",
        options: [
          "Brochures are expensive",
          "Customers trust what they can see in action",
          "You can't print anymore",
          "Regulations require it"
        ],
        correctIndex: 1,
        explanation: "Live demonstrations build trust and engagement. Customers seeing real-time COP readings are more convinced than reading specifications."
      }
    ]
  },
  
  3: {
    title: "Comfort & Control Benefits Quiz",
    passingScore: 60,
    questions: [
      {
        id: "3-1",
        question: "What is a common customer concern that you should address proactively?",
        options: [
          "The color of the unit",
          "'Will it keep my house as warm as my boiler?'",
          "The brand name",
          "Installation date"
        ],
        correctIndex: 1,
        explanation: "Comfort concerns are universal. Address them early by demonstrating stable, reliable heating that matches or exceeds boiler performance."
      },
      {
        id: "3-2",
        question: "How do heat pumps often provide MORE stable temperatures than boilers?",
        options: [
          "They're more expensive",
          "They modulate rather than cycling on/off dramatically",
          "They use better thermostats",
          "They have bigger radiators"
        ],
        correctIndex: 1,
        explanation: "Heat pumps typically modulate output smoothly, avoiding the temperature swings from boiler on/off cycling. This provides more consistent comfort."
      },
      {
        id: "3-3",
        question: "When a customer asks 'Is it complicated?', what's the best response?",
        options: [
          "Yes, you need training",
          "It depends on the model",
          "'Set your preferred temperature once, and the system handles everything automatically'",
          "Refer them to the manual"
        ],
        correctIndex: 2,
        explanation: "Emphasize simplicity and 'set and forget' operation. Most customers want reassurance that they won't need to constantly adjust things."
      },
      {
        id: "3-4",
        question: "What comfort feature helps sell multi-room systems?",
        options: [
          "Central control only",
          "Different temperatures for different rooms (zoning)",
          "Single temperature everywhere",
          "Manual adjustment required"
        ],
        correctIndex: 1,
        explanation: "Zoned comfort is a strong selling point: bathroom warm (24°C), bedroom cool (18°C), living room comfortable (20°C) — all automatically."
      }
    ]
  },
  
  4: {
    title: "Addressing Building Concerns Quiz",
    passingScore: 60,
    questions: [
      {
        id: "4-1",
        question: "How should you respond to 'Heat pumps only work in new houses'?",
        options: [
          "That's correct, we can't help",
          "'What matters is insulation quality, not building age — and older buildings can be upgraded'",
          "Avoid the topic",
          "Recommend a gas boiler instead"
        ],
        correctIndex: 1,
        explanation: "Turn the objection into an opportunity. Insulation can be upgraded regardless of building age, making heat pumps viable."
      },
      {
        id: "4-2",
        question: "When demonstrating envelope impact, what visual aid is most effective?",
        options: [
          "Technical data sheets",
          "Heat flux arrows showing where heat escapes",
          "Price lists",
          "Brand comparisons"
        ],
        correctIndex: 1,
        explanation: "Visual demonstration of heat flow is powerful. Customers can literally see the problem and solution, making insulation upgrades compelling."
      },
      {
        id: "4-3",
        question: "How can you turn building concerns into a sales opportunity?",
        options: [
          "Lower the price",
          "Offer insulation + heat pump packages for complete solution",
          "Recommend waiting until they move",
          "Only serve new buildings"
        ],
        correctIndex: 1,
        explanation: "Package deals (insulation upgrades + heat pump) solve the customer's problem completely and increase your sale value."
      },
      {
        id: "4-4",
        question: "What's the benefit of insulation upgrades for the customer beyond heat pump compatibility?",
        options: [
          "No benefit",
          "Reduced heating needs (50%+), lower bills, smaller equipment needed",
          "Only aesthetic",
          "Increases noise"
        ],
        correctIndex: 1,
        explanation: "Insulation delivers multiple benefits: dramatically lower energy use, smaller (cheaper) heat pump needed, and improved comfort year-round."
      }
    ]
  },
  
  5: {
    title: "Running Cost Demonstration Quiz",
    passingScore: 60,
    questions: [
      {
        id: "5-1",
        question: "How should you present running cost comparisons?",
        options: [
          "Use complicated formulas",
          "Local energy prices with concrete annual savings",
          "Avoid discussing costs",
          "Only mention equipment cost"
        ],
        correctIndex: 1,
        explanation: "Use local energy prices and calculate real €/year savings the customer will experience. Make the numbers personal and credible."
      },
      {
        id: "5-2",
        question: "What's the advantage of showing COP improvement from 3.0 to 4.5?",
        options: [
          "It's just technical detail",
          "It demonstrates 33% less electricity and proportionally lower bills",
          "Customers don't care",
          "It only matters for engineers"
        ],
        correctIndex: 1,
        explanation: "COP 3.0 to 4.5 is a 50% efficiency improvement, meaning ~33% less electricity use. Translate this to annual € savings for impact."
      },
      {
        id: "5-3",
        question: "Why show COP in mild weather conditions (e.g., 5°C outdoor)?",
        options: [
          "It's misleading",
          "To show that most heating happens at these conditions with HIGHER efficiency",
          "Regulations require it",
          "It's more honest"
        ],
        correctIndex: 1,
        explanation: "Most heating happens in mild weather, not at -10°C design conditions. Showing high COP at typical conditions demonstrates real-world efficiency."
      },
      {
        id: "5-4",
        question: "What timeline is most impactful for cost savings presentation?",
        options: [
          "Monthly",
          "Annual with 10-20 year projection",
          "Daily",
          "Hourly"
        ],
        correctIndex: 1,
        explanation: "Annual savings with long-term projection (10-20 years) shows the cumulative benefit. Thousands of euros saved over equipment lifetime is compelling."
      },
      {
        id: "5-5",
        question: "What should you prepare in advance for cost discussions?",
        options: [
          "Only brochures",
          "Local energy prices and a simple cost comparison tool/spreadsheet",
          "Nothing specific",
          "Just warranty information"
        ],
        correctIndex: 1,
        explanation: "Having local energy prices and a simple calculation tool ready makes your cost comparisons credible and specific to the customer's situation."
      }
    ]
  },
  
  6: {
    title: "Complete Customer Presentation Quiz",
    passingScore: 70,
    questions: [
      {
        id: "6-1",
        question: "What is the recommended order for a customer presentation?",
        options: [
          "Cost → Technical → Comfort",
          "Comfort → Efficiency → Value",
          "Technical specs → Warranty → Price",
          "Price → Discount → Close"
        ],
        correctIndex: 1,
        explanation: "Start with what customers care most about (comfort), build confidence with efficiency proof, then close with value proposition."
      },
      {
        id: "6-2",
        question: "What is your 'wow factor' in the demonstration?",
        options: [
          "Brand prestige",
          "COP 4.5+ performance",
          "Color options",
          "Quick installation"
        ],
        correctIndex: 1,
        explanation: "COP 4.5+ is impressive and translates directly to cost savings. Make this your memorable moment: 'Over 4 kWh heat for every 1 kWh electricity!'"
      },
      {
        id: "6-3",
        question: "How should you summarize benefits at the close?",
        options: [
          "Read the technical specifications",
          "Customer terms: comfort, efficiency, savings",
          "List model numbers",
          "Compare to competitors"
        ],
        correctIndex: 1,
        explanation: "Close by summarizing in customer benefit language: stable comfort, excellent efficiency, real cost savings. Ask for the decision."
      },
      {
        id: "6-4",
        question: "Why address objections proactively during the demo?",
        options: [
          "To confuse the customer",
          "To build trust by showing you understand their concerns",
          "To extend the meeting",
          "Regulations require it"
        ],
        correctIndex: 1,
        explanation: "Addressing common objections proactively shows expertise and builds trust. Customers appreciate that you understand their concerns."
      },
      {
        id: "6-5",
        question: "What separates a confident close from a hesitant one?",
        options: [
          "Talking faster",
          "Preparation, practice, and genuine belief in the product",
          "Offering larger discounts",
          "Using pressure tactics"
        ],
        correctIndex: 1,
        explanation: "Confidence comes from thorough preparation, practiced delivery, and genuine belief that the solution benefits the customer."
      }
    ]
  }
};

// ============================================================================
// QUIZ DATA ACCESS FUNCTIONS
// ============================================================================

/**
 * Get quiz data for a specific user type and quest
 * @param {string} userType - One of: installer, energyConsultant, salesPerson
 * @param {number} questId - The quest ID (1-6)
 * @returns {Object|null} Quiz data or null if not found
 */
export const getQuizForUserTypeAndQuest = (userType, questId) => {
  const quizDataMap = {
    installer: INSTALLER_QUIZ_DATA,
    energyConsultant: ENERGY_CONSULTANT_QUIZ_DATA,
    salesPerson: SALES_PERSON_QUIZ_DATA
  };
  
  const quizData = quizDataMap[userType] || INSTALLER_QUIZ_DATA;
  return quizData[questId] || null;
};

/**
 * Get quiz for a specific quest (backwards compatibility)
 * @param {number} questId - The quest ID
 * @param {string} userType - Optional user type (defaults to installer)
 * @returns {Object|null} Quiz data or null if not found
 */
export const getQuizForQuest = (questId, userType = 'installer') => {
  return getQuizForUserTypeAndQuest(userType, questId);
};

/**
 * Calculate score and pass/fail status
 * @param {number} questId - The quest ID
 * @param {Array} answers - Array of selected answer indices
 * @param {string} userType - The user type
 * @returns {Object|null} Quiz result object or null
 */
export const calculateQuizResult = (questId, answers, userType = 'installer') => {
  const quiz = getQuizForUserTypeAndQuest(userType, questId);
  if (!quiz) return null;

  let correctCount = 0;
  const results = quiz.questions.map((question, index) => {
    const isCorrect = answers[index] === question.correctIndex;
    if (isCorrect) correctCount++;
    return {
      questionId: question.id,
      isCorrect,
      selectedAnswer: answers[index],
      correctAnswer: question.correctIndex,
      explanation: question.explanation
    };
  });

  const score = Math.round((correctCount / quiz.questions.length) * 100);
  const passed = score >= quiz.passingScore;

  return {
    score,
    correctCount,
    totalQuestions: quiz.questions.length,
    passed,
    passingScore: quiz.passingScore,
    results
  };
};

/**
 * Get all quiz data for a user type
 * @param {string} userType - One of: installer, energyConsultant, salesPerson
 * @returns {Object} All quiz data for the user type
 */
export const getAllQuizDataForUserType = (userType) => {
  const quizDataMap = {
    installer: INSTALLER_QUIZ_DATA,
    energyConsultant: ENERGY_CONSULTANT_QUIZ_DATA,
    salesPerson: SALES_PERSON_QUIZ_DATA
  };
  
  return quizDataMap[userType] || INSTALLER_QUIZ_DATA;
};

const UserTypeQuizDataExports = {
  INSTALLER_QUIZ_DATA,
  ENERGY_CONSULTANT_QUIZ_DATA,
  SALES_PERSON_QUIZ_DATA,
  getQuizForQuest,
  getQuizForUserTypeAndQuest,
  calculateQuizResult,
  getAllQuizDataForUserType
};

export default UserTypeQuizDataExports;
